import type { UseFormReturn } from 'react-hook-form';
import { Label, Separator, Switch } from '@/components/ui';
import { ControlledInput, ControlledTextarea } from '@/components/controlled';
import { notify } from '@/functions';
import { PromoCodeBonusTypeEnum } from '@/enums';
import type { IPromoCodeFormData } from '@/interfaces';
import { Required, SectionTitle } from './PromoCodeFormParts';

type TProps = {
	form: UseFormReturn<IPromoCodeFormData>;
};

const bonusLabel = (type: PromoCodeBonusTypeEnum, value: number | '', lang: 'fr' | 'en') => {
	const amount = type === PromoCodeBonusTypeEnum.PERCENT ? `${value}%` : `${value}`;
	return lang === 'fr' ? `${amount} de pièces offertes` : `${amount} free coins`;
};

/* The words of the announcement (chat message and push), in both languages, starting from a template */
const PromoCodeAnnouncementFields = ({ form }: TProps) => {
	const { control, watch, setValue } = form;
	const shouldNotify = watch('notify');

	// the template needs the code and the bonus: no blanks in the message
	const handleNotifyChange = (checked: boolean) => {
		const { code, bonusType, bonusValue } = watch();
		if (checked && (!code.trim() || bonusValue === '')) {
			notify("Renseigne le code et le bonus avant de rédiger l'annonce.", 'info');
			return;
		}

		setValue('notify', checked);
		if (!checked || watch('title.fr')) return;

		const upperCode = code.toUpperCase();
		setValue('title.fr', `Code promo ${upperCode}`);
		setValue('title.en', `Promo code ${upperCode}`);
		setValue('message.fr', `Utilise le code ${upperCode} sur ton prochain achat de pièces : ${bonusLabel(bonusType, bonusValue, 'fr')}.`);
		setValue('message.en', `Use code ${upperCode} on your next coin purchase: ${bonusLabel(bonusType, bonusValue, 'en')}.`);
	};

	return (
		<>
			<Separator />

			<div className="space-y-3">
				<div className="flex items-start justify-between gap-4">
					<div className="space-y-0.5">
						<SectionTitle>Annonce</SectionTitle>
						<p className="text-xs text-muted-foreground">
							Chat Heyama et notification push, envoyés une fois au début de l'offre (ou tout de suite depuis la liste).
						</p>
					</div>
					<Switch checked={shouldNotify} onCheckedChange={handleNotifyChange} aria-label="Notifier les utilisateurs" />
				</div>

				{shouldNotify && (
					<div className="grid grid-cols-1 gap-4 rounded-lg border bg-muted/30 p-3 md:grid-cols-2">
						{(['fr', 'en'] as const).map(lang => (
							<div key={lang} className="space-y-3">
								<p className="text-xs font-medium">{lang === 'fr' ? 'Français' : 'English'}</p>
								<div className="flex flex-col gap-1">
									<Label>
										Titre <Required />
									</Label>
									<ControlledInput name={`title.${lang}`} control={control} rules={{ required: 'Champ requis' }} maxLength={80} />
								</div>
								<div className="flex flex-col gap-1">
									<Label>
										Message <Required />
									</Label>
									<ControlledTextarea
										name={`message.${lang}`}
										control={control}
										rows={2}
										rules={{ required: 'Champ requis' }}
										maxLength={300}
									/>
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</>
	);
};

export default PromoCodeAnnouncementFields;
